import { useState, useEffect } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { usePathname } from "next/navigation";

const SECTION_IDS = [
  "capabilities",
  "sectors",
  "terrakio",
  "our-work",
  "about",
  // Not nav items themselves: tracked so the highlight clears once the reader
  // scrolls past the last section the nav points at.
  "how-we-work",
  "contact",
];

export const useActiveNavItem = () => {
  const [activeItem, setActiveItem] = useState<string>("");
  const pathname = usePathname();
  const [lastPathname, setLastPathname] = useState(pathname);

  // Clear the highlight on navigation. Adjusting state during render is React's
  // recommended alternative to clearing it from inside the effect, which causes
  // a cascading render (react-hooks/set-state-in-effect). On its own this is not
  // enough -- the guard in the effect below is what makes the clear stick.
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setActiveItem("");
  }

  useEffect(() => {
    // Only activate nav items when on home page
    if (pathname !== "/") return;

    // Leaving / removes these sections, collapsing the ScrollSmoother wrapper,
    // so ScrollTrigger recalculates and fires onEnter on the way out -- after
    // the reset above. Cleanup is passive (post-paint) so `live` alone is too
    // late; the removal comes first, so isConnected is the guard that holds.
    let live = true;
    const writable = (element: HTMLElement) => live && element.isConnected;

    const triggers: ScrollTrigger[] = [];

    SECTION_IDS.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (!element) return;

      triggers.push(
        ScrollTrigger.create({
          trigger: element,
          start: "top center",
          end: "bottom center",
          onEnter: () => {
            if (writable(element)) setActiveItem(sectionId);
          },
          onEnterBack: () => {
            if (writable(element)) setActiveItem(sectionId);
          },
          // Functional update so the current value is read without needing
          // activeItem as an effect dependency, which would tear down and
          // rebuild every trigger on each highlight change.
          onLeaveBack: () => {
            if (writable(element)) {
              setActiveItem((prev) => (prev === sectionId ? "" : prev));
            }
          },
        })
      );
    });

    return () => {
      live = false;
      triggers.forEach((trigger) => trigger.kill());
    };
  }, [pathname]);

  return activeItem;
};
